import { useMemo } from 'react'
import { Alert, Card, Empty, Progress, Skeleton, Space, Tag, Tooltip, Typography, theme } from 'antd'
import { ClockCircleOutlined, ThunderboltOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { translateError } from '@/shared/api/errors'
import { queryKeys } from '@/shared/api/queryKeys'
import { usePlanDisplayIndex } from '@/shared/api/reference'
import { usePermissions } from '@/shared/auth/usePermissions'
import { formatBytes, formatDuration } from '@/shared/lib/format'
import { fetchActiveSessions } from '@/features/session/sessionApi'
import { fetchExpiringGrants, type ExpiringGrant } from './expiringApi'

/** Nechta qator ko'rsatiladi — bosh sahifa ro'yxat emas, signal */
const ROWS = 6
const FETCH_LIMIT = 50

interface SubscriberAttention {
  grant: ExpiringGrant
  grantCount: number
}

function bySubscriber(grants: readonly ExpiringGrant[]): SubscriberAttention[] {
  const groups = new Map<string, SubscriberAttention>()
  for (const grant of grants) {
    const group = groups.get(grant.subscriberId)
    if (group) group.grantCount += 1
    else groups.set(grant.subscriberId, { grant, grantCount: 1 })
  }
  return [...groups.values()]
}

/**
 * "E'tibor talab qiladi" — tugash arafasidagi paketlar.
 *
 * <h2>Nega bu panel bor</h2>
 * Panel ilgari abonent uzilgandan KEYIN xabar berardi. Bu esa kech:
 * abonent internetsiz qolgan va allaqachon qo'ng'iroq qilyapti. Tugash
 * arafasidagi paket esa SOTUV IMKONIYATI — operator oldindan bog'lanib
 * yangi paket taklif qila oladi.
 *
 * <h2>⚠️ "Kvotasi tugagan, lekin hali ulangan" — alohida belgilanadi</h2>
 * Bu nomuvofiqlik: abonentda hajm qolmagan, lekin sessiya hali ochiq.
 * Sabab avtomatik uzish kechikkani yoki CoA yetib bormagani bo'lishi
 * mumkin. Uni oddiy "tugayapti" qatoridan ajratmaslik nosozlikni
 * odatiy holat ichida ko'mib yuborardi.
 *
 * ⚠️ Bu belgi MIJOZDA hisoblanadi: faol sessiyalar ro'yxati allaqachon
 * bosh sahifada bor (`ActiveSessionsPanel` bilan bir xil kesh yozuvi),
 * ya'ni qo'shimcha so'rov yubormaydi. Server tomonda buni hisoblash
 * `radacct` (session servisi) va `access_grant` (subscriber servisi)
 * ni bog'lashni talab qilardi — bu ikki servis orasidagi bog'lanish.
 */
export function AttentionPanel() {
  const { t } = useTranslation()
  const { token } = theme.useToken()
  const { can } = usePermissions()
  const allowed = can('subscriber.read')
  const planNames = usePlanDisplayIndex(allowed && can('plan.read'))

  const expiring = useQuery({
    queryKey: queryKeys.grants.expiring,
    queryFn: () => fetchExpiringGrants({ withinHours: 24, remainingPercent: 10, limit: FETCH_LIMIT }),
    enabled: allowed,
    refetchInterval: 60_000,
  })

  // ⚠️ AYNAN o'sha kalit — `ActiveSessionsPanel` bilan kesh yozuvini
  // bo'lishadi, ya'ni bu panel qo'shimcha so'rov YUBORMAYDI
  const sessions = useQuery({
    queryKey: queryKeys.sessions.active,
    queryFn: () => fetchActiveSessions(),
    enabled: allowed,
  })

  const onlineLogins = useMemo(
    () => new Set((sessions.data ?? []).map((s) => s.username.toLowerCase())),
    [sessions.data],
  )

  const subscribers = useMemo(() => bySubscriber(expiring.data ?? []), [expiring.data])
  const rows = subscribers.slice(0, ROWS)

  if (!allowed) return null

  const label = (row: ExpiringGrant) => {
    if (row.reason === 'VOLUME' && row.remainingBytes !== null) {
      return formatBytes(row.remainingBytes)
    }
    if (row.expiresAt) {
      /*
        ⚠️ QOLGAN VAQT, mutlaq sana EMAS.

        Operator uchun "qancha qoldi" muhim, "soat 19:04 da tugaydi"
        emas — ikkinchisini o'qish uchun hozirgi soatni topib, ayirish
        kerak bo'ladi.

        `formatDuration` qayta ishlatiladi (`dayjs` `relativeTime`
        plagini loyihaga ulanmagan va bitta panel uchun uni qo'shish
        keraksiz bog'liqlik bo'lardi).
      */
      const secondsLeft = Math.max(0, Math.round((new Date(row.expiresAt).getTime() - Date.now()) / 1000))
      return formatDuration(secondsLeft)
    }
    return t('common.dash')
  }

  return (
    <Card
      title={t('dashboard.attention.title')}
      extra={<Link to="/subscribers">{t('dashboard.attention.all')}</Link>}
      size="small"
      style={{ height: '100%' }}
    >
      {expiring.isError ? (
        <Alert type="error" showIcon message={translateError(expiring.error, t)} />
      ) : expiring.isPending ? (
        <Skeleton active paragraph={{ rows: 4 }} />
      ) : rows.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('dashboard.attention.empty')}
        />
      ) : (
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
          {rows.map(({ grant: row, grantCount }) => {
            /*
              ⚠️ NOMUVOFIQLIK: hajm tugagan, lekin sessiya ochiq.

              Bu "tugayapti" emas, "tugagan va hali ham ulangan" —
              avtomatik uzish ishlamagan degani. Shuning uchun alohida
              rang va teg.
            */
            const exhaustedOnline =
              row.remainingBytes !== null &&
              row.remainingBytes <= 0 &&
              onlineLogins.has(row.login.toLowerCase())

            const planName = row.planId ? planNames.get(row.planId) : undefined

            return (
              <div key={row.subscriberId}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <Space size={4}>
                    <Link to={`/subscribers/${row.subscriberId}`}>
                      <Typography.Text ellipsis style={{ maxWidth: 140 }}>
                        {row.login}
                      </Typography.Text>
                    </Link>
                    {exhaustedOnline ? (
                      <Tooltip title={t('dashboard.attention.exhaustedOnlineHint')}>
                        <Tag color="error">{t('dashboard.attention.exhaustedOnline')}</Tag>
                      </Tooltip>
                    ) : null}
                  </Space>
                  <Space size={4} style={{ flexShrink: 0 }}>
                    {row.reason === 'TIME' ? (
                      <ClockCircleOutlined style={{ color: token.colorWarning }} />
                    ) : (
                      <ThunderboltOutlined style={{ color: token.colorWarning }} />
                    )}
                    <Typography.Text style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                      {label(row)}
                    </Typography.Text>
                  </Space>
                </div>
                {planName || grantCount > 1 ? (
                  <Typography.Text type="secondary" ellipsis style={{ display: 'block', fontSize: 12 }}>
                    {[planName, grantCount > 1 ? t('dashboard.attention.morePackages', { count: grantCount - 1 }) : null]
                      .filter(Boolean)
                      .join(' · ')}
                  </Typography.Text>
                ) : null}
                {row.remainingPercent !== null ? (
                  <Progress
                    percent={Math.max(0, Math.min(100, row.remainingPercent))}
                    showInfo={false}
                    size="small"
                    status={exhaustedOnline ? 'exception' : 'active'}
                    style={{ margin: 0, lineHeight: 1 }}
                  />
                ) : null}
              </div>
            )
          })}
          {subscribers.length > ROWS ? (
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              {t('dashboard.attention.more', { count: subscribers.length - ROWS })}
            </Typography.Text>
          ) : null}
        </Space>
      )}
    </Card>
  )
}
